import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
    PaperPlaneTilt, Sparkle, User, Robot, Plus, PencilSimple,
    Trash, ChatCircleText, List, X, Check, Coins, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel,
    AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const SUGGESTIONS = [
    "Suggest 3 AI startup ideas for rural India.",
    "Validate this: a B2B platform for restaurant inventory.",
    "Help me brainstorm names for a green energy startup.",
    "What are the best monetization models for a free mobile app?",
];

function relTime(iso) {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "now";
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
}

export default function AIChat() {
    const [sessions, setSessions] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [balance, setBalance] = useState(null);
    const [buyOpen, setBuyOpen] = useState(false);
    const [buyBusy, setBuyBusy] = useState(false);
    const scrollRef = useRef(null);

    const refreshBalance = async () => {
        try {
            const { data } = await api.get("/credits/balance");
            setBalance(data);
        } catch { /* ignore */ }
    };

    // Load balance + sessions on mount
    useEffect(() => {
        refreshBalance();
        api.get("/chat/sessions").then((r) => {
            setSessions(r.data);
            if (r.data.length > 0) setActiveId(r.data[0].id);
        }).catch(() => {});
    }, []);

    // Load messages whenever active session changes
    useEffect(() => {
        if (!activeId) { setMessages([]); return; }
        api.get("/chat/history", { params: { session_id: activeId } })
            .then((r) => {
                const flat = [];
                r.data.forEach((m) => {
                    flat.push({ role: "user", content: m.user_message });
                    flat.push({ role: "ai", content: m.ai_reply });
                });
                setMessages(flat);
            }).catch(() => setMessages([]));
    }, [activeId]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
    }, [messages, loading]);

    const refreshSessions = async () => {
        const { data } = await api.get("/chat/sessions");
        setSessions(data);
    };

    const newChat = async () => {
        try {
            const { data } = await api.post("/chat/sessions");
            setSessions((s) => [data, ...s]);
            setActiveId(data.id);
            setMessages([]);
            setHistoryOpen(false);
        } catch { toast.error("Could not create chat"); }
    };

    const send = async (text) => {
        const msg = (text || input).trim();
        if (!msg || loading) return;
        // Hard-block the request when we already know the wallet is empty/expired.
        if (balance && balance.tokens <= 0) {
            setBuyOpen(true);
            return;
        }
        setMessages((m) => [...m, { role: "user", content: msg }]);
        setInput(""); setLoading(true);

        // 2026-02: SSE streaming — token-by-token reveal like ChatGPT.
        // We fetch() the /chat/stream endpoint and progressively parse
        // the SSE frames. Fall back to non-streaming /chat if streaming
        // fails for any reason (auth cookie missing, network error).
        const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const streamUrl = `${baseUrl}/api/chat/stream`;
        // Placeholder AI message we mutate as tokens stream in.
        setMessages((m) => [...m, { role: "ai", content: "", streaming: true }]);

        try {
            const resp = await fetch(streamUrl, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: msg, session_id: activeId }),
            });
            if (!resp.ok || !resp.body) {
                // Fall through to legacy path on non-2xx.
                if (resp.status === 402) {
                    const errBody = await resp.json().catch(() => ({}));
                    setBuyOpen(true);
                    setMessages((m) => {
                        const copy = [...m];
                        const last = copy[copy.length - 1];
                        if (last?.streaming) last.content = `⚠️ ${errBody.detail || "You're out of AI credits."}`;
                        last.streaming = false;
                        return copy;
                    });
                    refreshBalance();
                    return;
                }
                throw new Error(`stream_status_${resp.status}`);
            }

            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                // Split on \n\n which delimits SSE frames.
                let idx;
                while ((idx = buffer.indexOf("\n\n")) !== -1) {
                    const frame = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);
                    // Parse frame: "event: X\ndata: {...}"
                    let evtName = "message";
                    let dataStr = "";
                    for (const line of frame.split("\n")) {
                        if (line.startsWith("event:")) evtName = line.slice(6).trim();
                        else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
                    }
                    if (!dataStr) continue;
                    let payload;
                    try { payload = JSON.parse(dataStr); } catch { continue; }
                    if (evtName === "token") {
                        const text = payload.text || "";
                        setMessages((m) => {
                            const copy = [...m];
                            const last = copy[copy.length - 1];
                            if (last?.streaming) last.content = (last.content || "") + text;
                            return copy;
                        });
                    } else if (evtName === "done") {
                        if (!activeId && payload.session_id) setActiveId(payload.session_id);
                        if (payload.balance) setBalance((b) => ({ ...(b || {}), ...payload.balance }));
                        setMessages((m) => {
                            const copy = [...m];
                            const last = copy[copy.length - 1];
                            if (last?.streaming) last.streaming = false;
                            return copy;
                        });
                        refreshSessions();
                        if (payload.balance && payload.balance.tokens > 0 && payload.balance.tokens < 10000) {
                            toast.warning(`Only ${payload.balance.tokens} tokens left — under 1 credit.`);
                        }
                    } else if (evtName === "error") {
                        throw new Error(payload.detail || "stream_error");
                    }
                }
            }
        } catch (e) {
            // Any streaming failure: swap the streaming placeholder for
            // a friendly error and try the non-streaming fallback so
            // users aren't stranded on flaky networks.
            setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last?.streaming && !last.content) {
                    last.content = "⚠️ Could not get a response.";
                    last.streaming = false;
                }
                return copy;
            });
            toast.error("AI is unavailable. Try again in a moment.");
        } finally {
            setLoading(false);
        }
    };

    const buyCredits = async () => {
        setBuyBusy(true);
        try {
            const { data } = await api.post("/payments/checkout", {
                plan: "credits_pack",
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch {
            toast.error("Could not start checkout. Try again.");
            setBuyBusy(false);
        }
    };

    const startRename = (s) => {
        setRenamingId(s.id);
        setRenameValue(s.title);
    };
    const commitRename = async () => {
        if (!renameValue.trim()) { setRenamingId(null); return; }
        try {
            await api.patch(`/chat/sessions/${renamingId}`, { title: renameValue.trim() });
            setSessions((arr) => arr.map((s) => s.id === renamingId ? { ...s, title: renameValue.trim() } : s));
            toast.success("Renamed");
        } catch { toast.error("Rename failed"); }
        finally { setRenamingId(null); }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/chat/sessions/${deleteTarget.id}`);
            const rest = sessions.filter((s) => s.id !== deleteTarget.id);
            setSessions(rest);
            if (activeId === deleteTarget.id) {
                setActiveId(rest[0]?.id || null);
                setMessages([]);
            }
            toast.success("Chat deleted");
        } catch { toast.error("Delete failed"); }
        finally { setDeleteTarget(null); }
    };

    const HistoryPanel = (
        <div className="flex flex-col h-full w-full min-w-0" data-testid="chat-history-panel">
            <div className="flex items-center justify-between p-4 border-b border-border">
                <h2 className="font-display font-bold text-sm uppercase tracking-[0.2em] text-muted-foreground">
                    History
                </h2>
                <button
                    onClick={() => setHistoryOpen(false)}
                    className="lg:hidden p-1.5 rounded-md hover:bg-black/5 dark:hover:bg-white/5"
                    aria-label="Close history"
                    data-testid="close-history"
                >
                    <X size={16} />
                </button>
            </div>
            <div className="p-3">
                <Button
                    onClick={newChat}
                    className="w-full brand-button justify-center"
                    data-testid="new-chat-button"
                >
                    <Plus size={16} weight="bold" className="mr-1" /> New chat
                </Button>
            </div>
            <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1">
                {sessions.length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-6 px-3">
                        No chats yet. Start a new conversation.
                    </div>
                )}
                {sessions.map((s) => {
                    const isActive = s.id === activeId;
                    const isRenaming = renamingId === s.id;
                    return (
                        <div
                            key={s.id}
                            data-testid={`chat-session-${s.id}`}
                            className={`group flex items-center gap-2 rounded-lg px-2 py-2 cursor-pointer transition-colors
                                ${isActive ? "bg-[#6C63FF]/10 text-[#6C63FF]" : "hover:bg-black/5 dark:hover:bg-white/5"}`}
                            onClick={() => { if (!isRenaming) { setActiveId(s.id); setHistoryOpen(false); } }}
                        >
                            <ChatCircleText size={16} weight="duotone" className="shrink-0" />
                            {isRenaming ? (
                                <div className="flex-1 flex items-center gap-1">
                                    <Input
                                        value={renameValue}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") commitRename();
                                            if (e.key === "Escape") setRenamingId(null);
                                        }}
                                        autoFocus
                                        className="h-7 text-xs"
                                        data-testid={`rename-input-${s.id}`}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <button
                                        onClick={(e) => { e.stopPropagation(); commitRename(); }}
                                        className="p-1 text-emerald-500 hover:bg-emerald-500/10 rounded"
                                        data-testid={`rename-save-${s.id}`}
                                    ><Check size={14} weight="bold" /></button>
                                </div>
                            ) : (
                                <>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{s.title}</div>
                                        <div className="text-[10px] text-muted-foreground">{relTime(s.updated_at)}</div>
                                    </div>
                                    <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 transition-opacity">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); startRename(s); }}
                                            className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded"
                                            data-testid={`rename-${s.id}`}
                                            aria-label="Rename chat"
                                        ><PencilSimple size={13} weight="duotone" /></button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}
                                            className="p-1 text-red-500 hover:bg-red-500/10 rounded"
                                            data-testid={`delete-${s.id}`}
                                            aria-label="Delete chat"
                                        ><Trash size={13} weight="duotone" /></button>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="flex h-[calc(100vh-4rem)] lg:h-screen" data-testid="ai-chat-page">
            {/* Desktop history rail */}
            <aside className="hidden lg:flex w-72 shrink-0 overflow-hidden border-r border-border bg-card/40">
                {HistoryPanel}
            </aside>

            {/* Mobile history drawer */}
            {historyOpen && (
                <>
                    <div
                        onClick={() => setHistoryOpen(false)}
                        className="lg:hidden fixed inset-0 bg-black/40 z-30"
                    />
                    <aside className="lg:hidden fixed top-0 left-0 z-40 h-screen w-72 bg-card border-r border-border">
                        {HistoryPanel}
                    </aside>
                </>
            )}

            <div className="flex-1 flex flex-col min-w-0">
                <header className="px-4 sm:px-6 lg:px-10 py-5 border-b border-border flex items-center gap-3">
                    <button
                        onClick={() => setHistoryOpen(true)}
                        className="lg:hidden p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/5"
                        data-testid="open-history"
                        aria-label="Open chat history"
                    ><List size={20} /></button>
                    <div className="h-10 w-10 rounded-lg bg-[#6C63FF] grid place-items-center text-white">
                        <Sparkle size={22} weight="fill" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1 className="font-display font-black text-xl lg:text-2xl tracking-tight truncate brand-text">
                            {sessions.find((s) => s.id === activeId)?.title || "AI Idea Studio"}
                        </h1>
                        <p className="text-xs text-muted-foreground">Generate, validate, and refine startup ideas with AI.</p>
                    </div>
                    {balance && (
                        <button
                            type="button"
                            onClick={() => setBuyOpen(true)}
                            data-testid="credit-balance-pill"
                            className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors shrink-0
                                ${balance.tokens <= 0
                                    ? "border-red-500/40 bg-red-500/10 text-red-500 hover:bg-red-500/20"
                                    : balance.tokens < 10000
                                        ? "border-[#FF8A00]/40 bg-[#FF8A00]/10 text-[#FF8A00] hover:bg-[#FF8A00]/20"
                                        : "border-[#6C63FF]/30 bg-[#6C63FF]/10 text-[#6C63FF] hover:bg-[#6C63FF]/20"}`}
                        >
                            <Coins size={14} weight="fill" />
                            <span>
                                {balance.tokens <= 0
                                    ? "0 credits"
                                    : `${balance.credits} credit${balance.credits === 1 ? "" : "s"}`}
                            </span>
                        </button>
                    )}
                </header>

                <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 py-6">
                    {messages.length === 0 && !loading && (
                        <div className="max-w-2xl mx-auto py-8">
                            <h2 className="font-display font-bold text-xl mb-4">Try one of these:</h2>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {SUGGESTIONS.map((s, i) => (
                                    <button
                                        key={i}
                                        onClick={() => send(s)}
                                        className="text-left rounded-xl border border-border p-4 text-sm hover:border-[#6C63FF]/50 hover:bg-[#6C63FF]/5 transition-colors"
                                        data-testid={`suggestion-${i}`}
                                    >{s}</button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="max-w-3xl mx-auto space-y-5">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`message-${i}`}>
                                <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0
                                    ${m.role === "user" ? "bg-[#FF8A00] text-white" : "bg-[#6C63FF] text-white"}`}>
                                    {m.role === "user" ? <User size={18} weight="bold" /> : <Robot size={18} weight="duotone" />}
                                </div>
                                <div className={`rounded-2xl px-4 py-3 max-w-[80%] whitespace-pre-wrap leading-relaxed ai-chat-message
                                    ${m.role === "user" ? "bg-[#FF8A00]/10 text-foreground" : "bg-card border border-border"}`}>
                                    {m.content}
                                    {m.streaming && (
                                        <span
                                            className="inline-block w-1.5 h-4 ml-0.5 -mb-0.5 bg-[#6C63FF] animate-pulse rounded-sm"
                                            data-testid="stream-cursor"
                                            aria-hidden="true"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                        {loading && !messages.some((mm) => mm.streaming) && (
                            <div className="flex gap-3" data-testid="ai-typing">
                                <div className="h-9 w-9 rounded-lg bg-[#6C63FF] grid place-items-center">
                                    <Robot size={18} className="text-white" />
                                </div>
                                <div className="rounded-2xl px-4 py-3 bg-card border border-border">
                                    <div className="flex gap-1">
                                        <span className="h-2 w-2 rounded-full bg-[#6C63FF] animate-bounce" />
                                        <span className="h-2 w-2 rounded-full bg-[#6C63FF] animate-bounce" style={{ animationDelay: "120ms" }} />
                                        <span className="h-2 w-2 rounded-full bg-[#6C63FF] animate-bounce" style={{ animationDelay: "240ms" }} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="border-t border-border bg-card/60 backdrop-blur-md p-4">
                    <form
                        onSubmit={(e) => { e.preventDefault(); send(); }}
                        className="max-w-3xl mx-auto flex gap-2"
                    >
                        <Textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            placeholder="Ask anything about startups, ideas, markets…"
                            className="min-h-[52px] resize-none"
                            data-testid="chat-input"
                        />
                        <Button type="submit" disabled={loading || !input.trim()} className="brand-button shrink-0 self-end" data-testid="chat-send">
                            <PaperPlaneTilt size={18} weight="fill" />
                        </Button>
                    </form>
                </div>
            </div>

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="delete-chat-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this chat?</AlertDialogTitle>
                        <AlertDialogDescription>
                            "{deleteTarget?.title}" and all its messages will be permanently removed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-chat-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="delete-chat-confirm"
                        >Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Buy AI Credits dialog */}
            <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
                <DialogContent data-testid="buy-credits-dialog" className="max-w-md">
                    <DialogHeader>
                        <div className={`h-12 w-12 rounded-xl grid place-items-center mb-3
                            ${(balance?.tokens ?? 1) <= 0
                                ? "bg-red-500/15 text-red-500"
                                : "bg-[#FF8A00]/15 text-[#FF8A00]"}`}>
                            {(balance?.tokens ?? 1) <= 0
                                ? <Warning size={26} weight="duotone" />
                                : <Coins size={26} weight="fill" />}
                        </div>
                        <DialogTitle className="font-display font-black text-2xl tracking-tight">
                            {(balance?.tokens ?? 1) <= 0
                                ? "You're out of AI credits"
                                : "Top up AI credits"}
                        </DialogTitle>
                        <DialogDescription className="leading-relaxed">
                            Buy a credit pack to keep chatting with the AI strategist.
                            Credits never expire — once you've used them all, simply buy another pack.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="rounded-xl border border-border bg-card p-5 my-2">
                        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground mb-1">
                            Credit pack · one-time
                        </div>
                        <div className="flex items-end justify-between">
                            <div className="font-display font-black text-4xl tracking-tighter">
                                {balance?.pack?.label || "$5.00"}
                            </div>
                            <div className="text-right">
                                <div className="font-display font-black text-2xl text-[#6C63FF]">100</div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">credits</div>
                            </div>
                        </div>
                        <ul className="mt-4 space-y-1.5 text-sm">
                            <li className="flex items-center gap-2">
                                <Check size={16} weight="bold" className="text-emerald-500" />
                                100 credits
                            </li>
                            <li className="flex items-center gap-2">
                                <Check size={16} weight="bold" className="text-emerald-500" />
                                Lifetime access — no expiry
                            </li>
                            <li className="flex items-center gap-2">
                                <Check size={16} weight="bold" className="text-emerald-500" />
                                Stacks on top of any unused credits
                            </li>
                        </ul>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setBuyOpen(false)}
                            data-testid="buy-credits-cancel"
                        >
                            Maybe later
                        </Button>
                        <Button
                            onClick={buyCredits}
                            disabled={buyBusy}
                            className="brand-button"
                            data-testid="buy-credits-confirm"
                        >
                            {buyBusy ? "Redirecting…" : `Buy 100 credits — ${balance?.pack?.label || "$5.00"}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
